import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ErrorMessage } from '@hookform/error-message';
import { RadioCircle } from '@components/radio-button';
import { Textarea } from '@components/form/textarea';
import { HrGray, HrLabel, HrParagraph, HrRed, HrTitleH4, Separate } from '@components/styles/hr-styles';
import { useTranslationWithPrefix } from '@/i18n/hooks';
import {
  CUSTOM_FIELD_TABS,
  CUSTOM_FIELD_TYPES
} from '@/vacancy_request/components/template-request-form/custom-fields/constants';
import {
  buildCustomFieldAnswerName,
  getCustomFieldsForTab,
  isCustomFieldRequired
} from '@/vacancy_request/components/template-request-form/custom-fields/mapper';

const ANSWER_MAX_LENGTH = 1000;

/*
 * Los campos fijos del formulario van en una grilla de dos columnas; los custom ocupan el ancho
 * completo, como en el diseño (990 de 990).
 */
const CustomFieldBlock = styled.div`
  grid-column: 1 / -1;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/* "Título de formularios" del diseño: el nombre del campo y una línea debajo. */
const FieldHeading = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ClosedQuestion = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

/**
 * Campos custom de una pestaña, en el formulario que completa el solicitante.
 *
 * Diseño: Figma "Solicitud de vacantes" → nodo 4670:3743. Cada campo se agrega al final de su
 * pestaña, precedido por un encabezado con su **nombre** y una línea. La **pregunta** es el label
 * del control: las abiertas usan un textarea de ancho completo con contador; las cerradas, radio
 * buttons apilados.
 *
 * El modo lectura no es un render aparte: igual que el resto del formulario, son los mismos
 * controles con `disabled`.
 *
 * Recibe el view model ya mapeado — no toca `templateConfig` ni la solicitud. La forma que devuelve
 * el api la conoce sólo `mapper.js`.
 */
export const CustomFieldsAnswers = ({ fields, tab, register, control, formState, watch, disabled }) => {
  const { t } = useTranslationWithPrefix('requestVacancies.requestTemplate.form.customFields');
  const tabFields = getCustomFieldsForTab(fields, tab);
  // Un solo observador para todas las respuestas de la pestaña: los radios necesitan saber cuál
  // está elegido y `watch` por campo dispararía una suscripción por opción.
  const answers = watch('customFieldsAnswers') || {};

  if (tabFields.length === 0) return null;

  return (
    <>
      {tabFields.map((field) => {
        const name = buildCustomFieldAnswerName(field);
        const required = isCustomFieldRequired(field);

        return (
          <CustomFieldBlock key={name}>
            <FieldHeading>
              <HrTitleH4 color={HrGray}>{field.name}</HrTitleH4>
              <Separate margin='0' />
            </FieldHeading>

            {field.type === CUSTOM_FIELD_TYPES.CLOSED ? (
              <ClosedQuestion>
                <HrLabel isRequired={required} cursor='auto' userSelect='none'>
                  {field.label}
                </HrLabel>

                {field.options.map((option) => (
                  <RadioCircle
                    key={option}
                    id={`${name}-${option}`}
                    name={name}
                    text={option}
                    value={option}
                    // A propósito NO se pasa `checked`: RadioCircle lo usaría como `defaultChecked`,
                    // que sólo aplica al montar. En lectura y edición la respuesta llega después,
                    // en un efecto, así que el radio quedaba sin marcar.
                    //
                    // `register` es una función cuyo retorno RadioCircle spreadea en el input, así
                    // que por ahí se inyectan las reglas de validación y un `checked` real. El input
                    // queda controlado —con el `onChange` que trae `register`— sin tocar el
                    // componente compartido ni remontar nada.
                    register={(fieldName) => ({
                      ...register(fieldName, required ? { required: t('answers.required') } : {}),
                      checked: answers[field.key] === option
                    })}
                    disabled={disabled}
                  />
                ))}

                <ErrorMessage
                  errors={formState.errors}
                  name={name}
                  render={({ message }) => <HrParagraph color={HrRed}>{message}</HrParagraph>}
                />
              </ClosedQuestion>
            ) : (
              <Textarea
                title={field.label}
                placeholder={t('answers.placeholder')}
                id={name}
                control={control}
                formState={formState}
                errorPosition='bottom'
                minHeight='140px'
                max={ANSWER_MAX_LENGTH}
                validate={{ required }}
                disabled={disabled}
              />
            )}
          </CustomFieldBlock>
        );
      })}
    </>
  );
};

CustomFieldsAnswers.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.string),
      requiredLevel: PropTypes.number
    })
  ).isRequired,
  tab: PropTypes.oneOf(Object.values(CUSTOM_FIELD_TABS)).isRequired,
  register: PropTypes.func.isRequired,
  control: PropTypes.object.isRequired,
  formState: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

CustomFieldsAnswers.defaultProps = {
  disabled: false
};
